import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Subject, Subscription, fromEvent, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AllergenMenuComponent } from '../allergens-menu/allergen-menu.component';
import { determineStorageUrl } from '../common/storage-tools';
import { NavItem } from '../models/nav-item.model';
import { RecipeGroup, RecipeSearchResult } from '../models/recipe-search-result.model';
import { Settings } from '../models/Settings';
import { FoodPortalStateService } from '../services/food-portal-state.service';
import { FoodPortalService } from '../services/food-portal.service';

type ResultsMode = "default" | "searchResults";

@Component({
    selector: 'pi-recipe-list',
    templateUrl: './recipe-list.component.html',
    styleUrls: ['./recipe-list.component.scss'],
    standalone: false
})
export class RecipeListComponent implements OnInit {

  allRecipes: RecipeSearchResult[];
  searchResults: RecipeSearchResult[];
  recipeGroups: RecipeGroup[];
  public searchTerms$ = new Subject<string>();
  settings: Settings;
  searchText: string;

  @ViewChild("AllergenMenu", { static: false }) allergenMenu: AllergenMenuComponent;


  stateData: {
    searchText: string;
  }

  resultsMode: ResultsMode = "default";

  @ViewChild("navItemsElement", { static: false }) navItemsElement: ElementRef;
  @ViewChildren("navItemElement") navItemElements: QueryList<ElementRef>;
  @ViewChild('recipeGroupsContainer', { static: false }) recipeGroupsElement: ElementRef;
  @ViewChildren('recipeGroupLabel') recipeGroupLabels: QueryList<ElementRef>;
  @ViewChildren('recipes') recipeBlocks: QueryList<ElementRef>;

  public navItems: NavItem[] | null;
  public storageUrl: string;

  constructor(
    private svc: FoodPortalService,
    private state: FoodPortalStateService,
  ) { }

  ngOnInit() {
    this.settings = this.state.portalSettings;
    this.allRecipes = this.state.recipes;
    this.stateData = this.state.recipeList;
    this.searchText = this.stateData.searchText;

    this.storageUrl = determineStorageUrl();

    // set up the debounce for the search text
    this.searchTerms$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
      ).subscribe(e => {
        this.searchText = e;
        this.stateData.searchText = e;
        this.searchTextUpdated();
      });

    this.navItems = this.state.navItems;
    this.createRecipeGroups();
    this.searchTextUpdated();
  }

  public clearSearchText(): void {
    this.searchText = "";
    this.stateData.searchText = "";
    this.searchTextUpdated();
  }

  private searchTextUpdated() {
    this.resultsMode = !!this.stateData.searchText ? "searchResults" : "default";
    this.filterRecipes();
  }

  private createRecipeGroups(): void {
    if (!this.state.navItems?.length) {
      this.recipeGroups = [{
        id: null,
        label: "Default",
        recipes: this.allRecipes
      }];
      return;
    }

    // group recipes by tag group
    this.recipeGroups = this.state.navItems.map(ni => {
      return {
        id: ni.id,
        label: ni.name,
        recipes: this.allRecipes.filter(r => ni.recipeIds.includes(r.id))
      };
    });

    // get the collection of 'other' recipes, ie recipes not tagged with any of the tags in navigation tag group
    let allTaggedRecipes = [];
    this.state.navItems.forEach(n => allTaggedRecipes = allTaggedRecipes.concat(n.recipeIds));
    const otherRecipes = this.allRecipes.filter(x => !allTaggedRecipes.includes(x.id));

    if (otherRecipes.length) {

      // add a nav item for the 'other' recipes
      if (!this.state.navItems.some(x => x.id === -1)) {
        this.state.navItems.push({
          id: -1,
          name: "Other",
          recipeIds: otherRecipes.map(x => x.id)
        });
      }

      // add a group
      this.recipeGroups.push({
        id: -1,
        label: "Other",
        recipes: otherRecipes
      });

    }

  }

  ngAfterViewInit() {

    this.setupScrollWatching();

    this.recipeBlocks.changes.subscribe(() => {
      this.setupScrollWatching();
    });
  }

  public searchInputFocus($event: FocusEvent) {
    const input = $event.target as HTMLInputElement;
    input.select();
  }

  /** Controls visbility of recipe group titles, hidden if there is only one group */
  public get showGroupTitles(): boolean {
    return this.recipeGroups.length > 1;
  }

  selectedAllergies() {
    return this.svc.selectedAllergens.length > 0 ? `Allergies (${this.svc.selectedAllergens.length})` : `Allergies?`;
  }

  selectedAllergenCount() {
    return this.svc.selectedAllergens.length > 0 ? this.svc.selectedAllergens.length : 0;
  }

  filterRecipes() {
    let res = [...this.allRecipes];
    const words = this.stateData.searchText.toLowerCase().split(" ");
    words.forEach(word => {
      res = word ? res.filter(c => c.name.toLowerCase().includes(word)) : res;
    });

    this.searchResults = [...res];
  }

  ngOnDestroy() {
    if (this.navDragSubscription) {
      this.navDragSubscription.unsubscribe();
    }
  }


  openMenu() {
    this.state.openMenu$.next();
  }

  openAllergenMenu() {
    this.allergenMenu.showMenu = true;
  }



  //#region Navigation drag/drop


  public get selectedNavItem(): NavItem | null {
    return this.state.selectedNavItem;
  }
  public set selectedNavItem(val: NavItem | null) {
    this.state.selectedNavItem = val;
  }

  private isClickScrolling = false;

  public selectNavItem(ni: NavItem, index: number): void {
    this.selectedNavItem = ni;
    this.isClickScrolling = true;
    timer(1000).subscribe(() => this.isClickScrolling = false);

    // scroll to the recipe group label with the matching index
    const elem = Array.from(this.recipeBlocks)[index];
    const nElem = elem.nativeElement;
    nElem.scrollIntoView({ behavior: 'smooth', block: 'start' });

    this.navItemActivated(index);

  }

  navItemActivated(index: number) {
    const navItem = this.navItemElements.toArray()[index].nativeElement;
    navItem.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
  }

  private navDragSubscription?: Subscription;
  navOffsetX = 0;
  startNavX = 0;

  public startNavDrag(event: MouseEvent): void {

    this.startNavX = event.clientX;

    const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove');
    const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

    this.navDragSubscription = mouseMove$
      .pipe(takeUntil(mouseUp$))
      .subscribe((moveEvent: MouseEvent) => this.handleNavDrag(moveEvent));

    mouseUp$.subscribe(() => this.stopNavDrag());
  }

  public handleNavDrag(event: MouseEvent): void {
    const deltaX = event.clientX - this.startNavX;
    this.navOffsetX += deltaX;
    this.startNavX = event.clientX;

    // Optional: Add bounds to prevent over-dragging
    const maxOffset = 0;
    const containerWidth = this.navItemsElement.nativeElement?.clientWidth || 0;
    const panelWidth = this.navItems.length * 100; // Assuming each nav item is 100px wide
    const minOffset = Math.min(0, containerWidth - panelWidth);

    this.navOffsetX = Math.min(maxOffset, Math.max(this.navOffsetX, minOffset));
  }

  public stopNavDrag(): void {
    if (this.navDragSubscription) {
      this.navDragSubscription.unsubscribe();
      this.navDragSubscription = undefined;
    }
  }

  public startTouchDrag(event: TouchEvent): void {
    this.startNavX = event.touches[0].clientX;

    const touchMove$ = fromEvent<TouchEvent>(document, 'touchmove');
    const touchEnd$ = fromEvent<TouchEvent>(document, 'touchend');

    this.navDragSubscription = touchMove$
      .pipe(takeUntil(touchEnd$))
      .subscribe((touchEvent: TouchEvent) => this.handleTouchDrag(touchEvent));

    touchEnd$.subscribe(() => this.stopTouchDrag());
  }

  public handleTouchDrag(event: TouchEvent): void {
    const deltaX = event.touches[0].clientX - this.startNavX;
    this.navOffsetX += deltaX;
    this.startNavX = event.touches[0].clientX;

    // Optional: Add bounds to prevent over-dragging
    const maxOffset = 0;
    const containerWidth = this.navItemsElement.nativeElement?.clientWidth || 0;
    const panelWidth = this.navItems.length * 100; // Assuming each nav item is 100px wide
    const minOffset = Math.min(0, containerWidth - panelWidth);

    this.navOffsetX = Math.min(maxOffset, Math.max(this.navOffsetX, minOffset));
  }

  public stopTouchDrag(): void {
    if (this.navDragSubscription) {
      this.navDragSubscription.unsubscribe();
      this.navDragSubscription = undefined;
    }
  }

  setupScrollWatching() {

    const container = this.recipeGroupsElement.nativeElement;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (this.isClickScrolling) return;
          const element = entry.target as HTMLElement;

          const entryIndex = this.recipeGroupLabels.toArray().findIndex(x => x.nativeElement === element);
          if (entry.isIntersecting) {
            this.selectedNavItem = this.navItems[entryIndex];
            this.navItemActivated(entryIndex);
          }
        });
      },
      {
        root: container,
        threshold: 0.1,
      }
    );

    this.recipeGroupLabels.forEach(child => observer.observe(child.nativeElement));


  }


  //#endregion

}
